import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Tooltip from '@material-ui/core/Tooltip';
import Skeleton from '@material-ui/lab/Skeleton';

import { DIMENSIONS } from '../../styles/constants';
import { RANGE_FILTERS } from '../Filterbar/constants';
import { buildSelectItems, checkRangeFiltersData, generateChartData } from './utils';
import { useTableRowStyles, useTableCellStyles } from '../Card/styles';
import { useStyles } from './styles';
import Card from '../Card';
import Select from '../Select';
import DistributionChart from './ColumnAmChart';

const DesignsDistribution = () => {
	const { t } = useTranslation();
	const classes = useStyles();
	const tableRowClasses = useTableRowStyles();
	const tableCellClasses = useTableCellStyles();
	const [designAttribute, setDesignAttribute] = useState('');
	const [chartData, setChartData] = useState([]);
	const [stateDataPrepared, setStateDataPrepared] = useState(false);
	const rangeFiltersMinMax = useSelector(state => state.designList.rangeFiltersMinMax);
	const items = useSelector(state => state.designList.items);
	const filteredItems = useSelector(state => state.designList.filteredItems);
	const progressActive = useSelector(state => state.tabbar.progressActive);

	const isRangeFiltersDataEmpty = useCallback(() => checkRangeFiltersData(rangeFiltersMinMax), [rangeFiltersMinMax]);

	const generateChartInput = useCallback(
		filterName => generateChartData(filterName, rangeFiltersMinMax),
		[rangeFiltersMinMax]
	);

	useEffect(() => {
		const rangeFiltersNames = Object.keys(rangeFiltersMinMax);

		if (!isRangeFiltersDataEmpty()) {
			setDesignAttribute(rangeFiltersNames[0]);
			setChartData(generateChartInput(rangeFiltersNames[0]));

			setStateDataPrepared(true);
		}
	}, [generateChartInput, isRangeFiltersDataEmpty, rangeFiltersMinMax]);

	const handleAttributeChange = event => {
		setDesignAttribute(event.target.value);
		setChartData(generateChartInput(event.target.value));
	};

	return (
		<Card
			title={
				progressActive ? (
					<Skeleton height={DIMENSIONS.SKELETON_TITLE_HEIGHT} />
				) : (
					t('components.select.calculationResults.title')
				)
			}>
			<Table>
				<TableBody>
					{progressActive ? (
						<Skeleton height={DIMENSIONS.SKELETON_TEXT_HEIGHT} />
					) : (
						<TableRow classes={tableRowClasses}>
							<TableCell classes={tableCellClasses}>
								<Typography variant="body2" color="textSecondary" className={classes.title}>
									{t('components.select.matchingDesigns')}
								</Typography>
							</TableCell>

							<TableCell classes={tableCellClasses}>
								<Typography display="inline" variant="body1" className={classes.title}>
									<Typography
										display="inline"
										variant="body1"
										color="textSecondary"
										id={'CalculationResults__matching_designs_value'}>
										{filteredItems.length}
									</Typography>

									<Typography display="inline" variant="body1" color="textSecondary">
										{' ' + t('components.select.calculationResults.of') + ' ' + items.length}
									</Typography>
								</Typography>
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>

			{progressActive ? (
				<Skeleton height={DIMENSIONS.DISTRIBUTION_CHART_HEIGHT} />
			) : (
				stateDataPrepared && (
					<div className={classes.chartContainer}>
						<DistributionChart
							attribute={RANGE_FILTERS.find(rFilter => rFilter.name === designAttribute)}
							data={chartData}
							rangeFiltersMinMax={rangeFiltersMinMax}
						/>
					</div>
				)
			)}

			{progressActive ? (
				<Fragment>
					<Skeleton height={DIMENSIONS.SKELETON_BUTTON_HEIGHT} />
				</Fragment>
			) : (
				stateDataPrepared && (
					<Tooltip title={t('components.select.designAttributes')}>
						<div className={classes.chartContainer}>
							<Select
								id="design-att-select-label"
								value={designAttribute}
								items={buildSelectItems(rangeFiltersMinMax)}
								fullWidth
								onChange={handleAttributeChange}
								disabled={isRangeFiltersDataEmpty()}
							/>
						</div>
					</Tooltip>
				)
			)}
		</Card>
	);
};

export default DesignsDistribution;
